import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box, BoxProps, Flex, InputProps, Grid } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { TranslatedProductSearchUnit, UnitType } from "shared/models/productSearchUnit/types";
import { CheckButton } from "ui/CheckButton/CheckButton";
import { Form } from "ui/Form/Form";
import { WarningRoundIcon, WarningThreeIcon } from "ui/Icons/Icons";
import { InboundFruitOrVeggyChips } from "ui/InboundFruitOrVeggyChips/InboundFruitOrVeggyChips";
import { ItemsExpectedBar } from "ui/ItemsExpectedBar/ItemsExpectedBar";
import { Number } from "ui/Number/Number";
import { ProductInformation } from "ui/ProductInformation/ProductInformation";
import { ProductQuantityInput } from "ui/ProductQuantityInput/ProductQuantityInput";
import { Shelf } from "ui/Shelf/Shelf";
import { Status } from "ui/Status/Status";
import { SlideFade } from "ui/Transitions/Transitions";
import { TitleXS } from "ui/Typography/Typography";
import { isNotNullNorUndefined } from "utils/tsHelpers";

const getPartiallyInboundedBarProps = (animated: boolean) =>
  animated ? { bg: "purple.500", transition: "all 1s", opacity: "0" } : { bg: "purple.500" };

export type QuantityFieldProps = {
  inputProps: InputProps;
  onSubmitForm: React.FormEventHandler;
};

export type TranslatedInboundUnit = Pick<
  TranslatedProductSearchUnit,
  | "shelfLetter"
  | "shelfNumber"
  | "productName"
  | "productSku"
  | "productImageUrl"
  | "countryOfOriginName"
  | "isProductBio"
  | "ean"
  | "stockOnShelf"
>;

export type InboundPreDroppingListItemCardProps = {
  elementRef?: (element: HTMLDivElement) => void;
  translatedInboundUnit: TranslatedInboundUnit;
  stockUpdatePlanTotal?: number;
  quantityFieldProps?: QuantityFieldProps;
  isPartiallyOutbounded?: boolean;
  showPartiallyOutboundedAnimation?: boolean;
  displayQuantity: number;
  isStockUpdateComplete?: boolean;
  displayAsHandlingUnit: boolean;
  isEditingQuantity?: boolean;
  isEditingTotal?: boolean;
  isMatchedtDesadvItem?: boolean;
  expectedUnitType?: UnitType;
  expectedNumberOfUnits?: number;
  isQuantityMatchingExpectation?: boolean | null; // null means user hasn't finalized the quantity
  isTotalQuantityMatchingExpectation?: boolean;
  isEnabledFilter?: boolean;
  onClickTotalQuantityNumber?: () => void;
  onConfirmQuantity?: () => void;
  totalQuantityFieldProps?: QuantityFieldProps;
} & BoxProps;

export function InboundPreDroppingListItemCard({
  elementRef,
  translatedInboundUnit: {
    shelfLetter,
    shelfNumber,
    productName,
    productSku,
    productImageUrl,
    countryOfOriginName,
    isProductBio,
    ean,
    stockOnShelf,
  },
  displayQuantity,
  displayAsHandlingUnit,
  stockUpdatePlanTotal,
  quantityFieldProps,
  isPartiallyOutbounded = false,
  showPartiallyOutboundedAnimation = false,
  isStockUpdateComplete = false,
  isEditingQuantity = false,
  isEditingTotal = false,
  isMatchedtDesadvItem = false,
  expectedUnitType,
  expectedNumberOfUnits,
  isQuantityMatchingExpectation,
  isTotalQuantityMatchingExpectation = true,
  isEnabledFilter = true,
  onClickTotalQuantityNumber = () => {},
  onConfirmQuantity = () => {},
  totalQuantityFieldProps,
  ...rest
}: InboundPreDroppingListItemCardProps) {
  const intl = useIntl();
  const {
    singleUnitsLabel,
    handlingUnitsLabel,
    bioLabel,
    nonBioLabel,
    partialOutboundLabel,
    totalUnitsLabel,
    inboundedLabel,
  } = useMemo(
    () => ({
      singleUnitsLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.single-units-label",
      }),
      handlingUnitsLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.handling-units-label",
      }),
      totalUnitsLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.total-handling-units-label",
      }),
      bioLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.bio-label",
      }),
      nonBioLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.non-bio-label",
      }),
      partialOutboundLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.partial-outbound-label",
      }),
      inboundedLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.inbounded-label",
      }),
    }),
    [intl],
  );
  const shouldShowTotalHandlingUnits =
    displayAsHandlingUnit && isNotNullNorUndefined(stockUpdatePlanTotal);

  const isFruitOrVeggy = isNotNullNorUndefined(countryOfOriginName);

  const partiallyOutboundedAnimTimeout = useRef<NodeJS.Timeout | null>(null);

  const [partiallyOutboundedAnimIn, setPartiallyOutboundedAnimIn] = useState(
    showPartiallyOutboundedAnimation,
  );
  const [productInfoLeftSideBarProps, setProductInfoLeftSideBarProps] = useState<BoxProps>(() =>
    getPartiallyInboundedBarProps(showPartiallyOutboundedAnimation),
  );

  const onPartiallyOutboundedAnimationComplete = useCallback(() => {
    partiallyOutboundedAnimTimeout.current = setTimeout(() => {
      setPartiallyOutboundedAnimIn(false);
      setProductInfoLeftSideBarProps({ ...productInfoLeftSideBarProps, opacity: "1" });
      partiallyOutboundedAnimTimeout.current = null;
    }, 1000);
  }, [setPartiallyOutboundedAnimIn, setProductInfoLeftSideBarProps, productInfoLeftSideBarProps]);

  useEffect(() => {
    return () => {
      // Cleanup if animation is not finished when component unmounts
      if (partiallyOutboundedAnimTimeout.current) {
        clearTimeout(partiallyOutboundedAnimTimeout.current);
      }
    };
  }, []);

  const displayedStatus = useMemo(() => {
    if (isStockUpdateComplete) {
      return <Status bg="veggie.500" textColor="white" label={inboundedLabel} />;
    }
    if (isFruitOrVeggy) {
      return (
        <InboundFruitOrVeggyChips
          bioLabel={bioLabel}
          nonBioLabel={nonBioLabel}
          countryOfOriginName={countryOfOriginName}
          isBio={!!isProductBio}
        />
      );
    }

    return null;
  }, [
    isStockUpdateComplete,
    isFruitOrVeggy,
    bioLabel,
    nonBioLabel,
    countryOfOriginName,
    isProductBio,
    inboundedLabel,
  ]);

  const quantityDisplay = useMemo(() => {
    if (isEditingTotal) {
      return (
        <Number
          content={displayQuantity.toString()}
          color="black"
          bgColor="white"
          data-testid="product-number-of-units"
        />
      );
    }

    if (isStockUpdateComplete) {
      return (
        <Number
          content={displayQuantity.toString()}
          color="grey.800"
          bgColor="grey.100"
          data-testid="unit-quantity"
        />
      );
    }

    if (!quantityFieldProps) {
      return (
        <Number
          content={displayQuantity.toString()}
          color="grey.800"
          bgColor={displayAsHandlingUnit ? "orange.100" : "marine.100"}
          data-testid="unit-quantity"
        />
      );
    }
    return (
      <Form onSubmit={quantityFieldProps.onSubmitForm}>
        <ProductQuantityInput
          displayAsHandlingUnit={displayAsHandlingUnit}
          {...quantityFieldProps.inputProps}
          hasWarning={isQuantityMatchingExpectation === false}
          data-testid="product-quantity-input"
        />
      </Form>
    );
  }, [
    isEditingTotal,
    isStockUpdateComplete,
    quantityFieldProps,
    displayAsHandlingUnit,
    isQuantityMatchingExpectation,
    displayQuantity,
  ]);

  const displayedShelf =
    isNotNullNorUndefined(shelfLetter) && isNotNullNorUndefined(shelfNumber) ? (
      <Shelf gridColumnStart="3" letter={shelfLetter} number={shelfNumber} />
    ) : (
      <Status bg="grey.200" textColor="black" gridColumnStart="3" label="N/A" />
    );

  function quantityConfirmation() {
    if (isEditingTotal || isEditingQuantity) return null;
    if (isQuantityMatchingExpectation !== false) {
      return <CheckButton checked={!!isQuantityMatchingExpectation} onClick={onConfirmQuantity} />;
    }
    return (
      <WarningRoundIcon
        color="red.500"
        w="32px"
        h="40px"
        data-testid="quantity-expectation-warning"
      />
    );
  }

  const showLeftSideBar = isPartiallyOutbounded || isStockUpdateComplete;
  const css = showLeftSideBar
    ? {
        borderLeft: "8px",
        borderLeftColor: isPartiallyOutbounded ? "purple.500" : "veggie.500",
        borderLeftRadius: "8px",
      }
    : {};

  const showItemsExpectedBar = !showLeftSideBar && isMatchedtDesadvItem;

  return (
    <Box
      ref={elementRef}
      bg="white"
      filter={isEnabledFilter && isStockUpdateComplete ? "grayscale(100%)" : undefined}
      data-testid={`pre-droppping-product-card-${ean}`}
      {...css}
      {...rest}
    >
      <Grid position="relative" p="s200" templateColumns="auto 1fr min-content">
        {showItemsExpectedBar ? (
          <ItemsExpectedBar
            isUnitTypeHandlingUnit={displayAsHandlingUnit}
            numberOfUnits={expectedNumberOfUnits ?? 0}
          />
        ) : (
          displayedStatus
        )}
        {showPartiallyOutboundedAnimation && (
          <Box position="absolute" left="0" right="0">
            <SlideFade
              onAnimationComplete={onPartiallyOutboundedAnimationComplete}
              unmountOnExit
              in={partiallyOutboundedAnimIn}
              offsetX="-300"
              offsetY={0}
            >
              <Status
                bg="purple.500"
                textColor="white"
                label={partialOutboundLabel}
                whiteSpace="nowrap"
              />
            </SlideFade>
          </Box>
        )}
        {displayedShelf}
      </Grid>
      <ProductInformation
        bg="grey.100"
        name={productName ?? ""}
        sku={productSku}
        showSKU={false}
        stock={stockOnShelf}
        imageUrl={productImageUrl ?? ""}
      />
      <Grid templateColumns="1fr 1fr min-content" p="s200">
        <Flex direction="column">
          <TitleXS mb="s50" color="grey.800">
            {displayAsHandlingUnit ? handlingUnitsLabel : singleUnitsLabel}
          </TitleXS>
          <Flex gap="s150">
            {quantityDisplay}
            {isMatchedtDesadvItem && quantityConfirmation()}
          </Flex>
        </Flex>
        {shouldShowTotalHandlingUnits && (
          <Flex gridColumnStart={3} flex={1}>
            <Flex direction="column" alignItems="center">
              <TitleXS mb="s50" color="grey.800">
                {totalUnitsLabel}
              </TitleXS>
              {isEditingTotal ? (
                <Form onSubmit={totalQuantityFieldProps?.onSubmitForm}>
                  <ProductQuantityInput
                    displayAsHandlingUnit
                    {...totalQuantityFieldProps?.inputProps}
                    data-testid="total-quantity-input"
                  />
                </Form>
              ) : (
                <Number
                  color={isTotalQuantityMatchingExpectation ? "black" : "red.500"}
                  bgColor="white"
                  content={stockUpdatePlanTotal.toString()}
                  onClick={onClickTotalQuantityNumber}
                  data-testid="product-handling-units-total-value"
                />
              )}
            </Flex>
            {!isTotalQuantityMatchingExpectation && (
              <WarningThreeIcon
                color="red.500"
                h="100%"
                w="20px"
                pt="23px"
                data-testid="total-quantity-warning"
              />
            )}
          </Flex>
        )}
      </Grid>
    </Box>
  );
}
